import React, { useMemo, useEffect, useRef, useState } from "react";
import { FiX, FiTrash2 } from "react-icons/fi";
import ChatHeader from "./ChatHeader.jsx";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";

const ChatUI = ({
  activeUser,
  currentUser,
  conversations,
  onlineIds,
  socketConnected,
  uploadingFiles,
  uploadProgress,
  fileInputRef,
  messagesEndRef,
  scrollContainerRef,
  onSendMessage,
  onTyping,
  onFileUpload,
  onBack,
  onScroll,
  updateMessageStatus,
  markAllAsRead,
  clearChat,
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const activeConv = conversations[activeUser.id] || {};
  const onlineSet = useMemo(
    () => new Set((onlineIds || []).map(String)),
    [onlineIds]
  );
  const isLoadingRef = useRef(false);

  const groupedMessages = useMemo(() => {
    if (!activeConv?.messages) return [];

    const groups = [];
    let currentDate = null;
    let currentGroup = [];

    const formatMessageDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (d.toDateString() === today.toDateString()) return "Today";
      if (d.toDateString() === yesterday.toDateString()) return "Yesterday";

      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
      });
    };

    [...activeConv.messages]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach((msg, index) => {
        const msgDate = formatMessageDate(msg.createdAt || msg.timestamp);

        if (msgDate !== currentDate) {
          if (currentGroup.length > 0) {
            groups.push({
              date: currentDate,
              messages: [...currentGroup],
            });
          }
          currentDate = msgDate;
          currentGroup = [msg];
        } else {
          currentGroup.push(msg);
        }

        if (index === activeConv.messages.length - 1) {
          groups.push({
            date: currentDate,
            messages: currentGroup,
          });
        }
      });

    return groups;
  }, [activeConv?.messages]);

  const handleClearChat = () => {
    if (
      window.confirm("Clear all messages in this chat? This cannot be undone.")
    ) {
      clearChat(activeUser.id);
      setShowClearConfirm(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.target;
    if (!activeUser || !el || isLoadingRef.current) return;

    if (el.scrollTop < 100 && activeConv.hasMore) {
      isLoadingRef.current = true;
      onScroll?.(activeUser.id, (activeConv.page || 1) + 1);
      setTimeout(() => {
        isLoadingRef.current = false;
      }, 500);
    }
  };

  useEffect(() => {
    if (activeUser && activeConv.unread > 0) {
      markAllAsRead?.();
    }
  }, [activeUser, activeConv.unread, markAllAsRead]);

  return (
    <div className="flex flex-col h-full">
      {/* Chat Header with Clear Button */}
      <div className="flex-shrink-0 px-4 py-3.5 bg-gradient-to-r from-yellow-300 to-yellow-200 flex items-center justify-between border-b-2 border-yellow-400 shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 text-blue-900 hover:bg-yellow-300 rounded-lg sm:hidden transition-all"
          >
            <FiX className="text-lg font-bold" />
          </button>
          <ChatHeader
            activeUser={activeUser}
            onlineSet={onlineSet}
            typing={activeConv.typing}
            socketConnected={socketConnected}
            currentUser={currentUser}
          />
        </div>

        {/* Clear Chat Button */}
        <div className="relative">
          <button
            onClick={() => setShowClearConfirm(!showClearConfirm)}
            className="p-2 text-blue-900 hover:bg-yellow-300 rounded-lg transition-all"
            title="Clear chat"
          >
            <FiTrash2 className="text-lg" />
          </button>

          {showClearConfirm && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white border-2 border-yellow-400 rounded-lg shadow-xl z-10">
              <div className="p-3">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Clear all messages?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleClearChat}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-1.5 rounded transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-bold py-1.5 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-white to-blue-50"
        onScroll={handleScroll}
        ref={scrollContainerRef}
      >
        {activeConv.loaded === false && activeConv.messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin text-blue-900 text-2xl font-bold">
              ⟳
            </div>
          </div>
        ) : (
          <ChatMessages
            groupedMessages={groupedMessages}
            messagesEndRef={messagesEndRef}
            currentUser={currentUser}
            activeUser={activeUser}
            uploadingFiles={uploadingFiles}
            uploadProgress={uploadProgress}
            updateMessageStatus={updateMessageStatus}
          />
        )}
      </div>

      {/* Chat Input */}
      <div className="flex-shrink-0">
        <ChatInput
          disabled={!socketConnected}
          onSend={(text) => onSendMessage(text)}
          onTyping={onTyping}
          onFileSelect={(file) => onFileUpload(file)}
          fileInputRef={fileInputRef}
        />
      </div>
    </div>
  );
};

export default ChatUI;
